import React from "react";
import { wire } from "react-hot-wire";

import { WithTranslation, withTranslation } from "react-i18next";

import { Table } from "components/common/table/Table";
import { Query } from "components/common/table/types";

import { JobService } from "services/job/Job.service";
import { DrawerService } from "services/drawer/Drawer.service";
import { JobStateChip } from "../common/JobStateChip";
import { Job, JobState, JobTrigger } from "../../../models/job/Job";

function JobList({
  jobService,
  drawerService,
  t
}: {
  jobService: JobService;
  drawerService: DrawerService;
} & WithTranslation) {
  const i18nKey = "job.list";

  return (
    <div className="m-3">
      <Table
        title={t(`${i18nKey}.title`)}
        dataFetcher={(query: Query) => jobService.list({ query })}
        options={{
          search: true,
          pagination: true,
          sortingMode: "single",
          refresh: 5000
        }}
        filters={[
          {
            type: "date-range",
            name: "createdAt",
            label: t(`${i18nKey}.filter.createdAt.label`)
          },
          {
            type: "select",
            name: "type",
            label: t(`${i18nKey}.filter.type.label`),
            options: [
              {
                value: "",
                label: t(`${i18nKey}.filter.type.option.all`)
              },
              {
                value: "main",
                label: t(`${i18nKey}.filter.type.option.main`),
                default: true
              },
              {
                value: "children",
                label: t(`${i18nKey}.filter.type.option.children`)
              }
            ]
          },
          {
            type: "select",
            name: "state",
            label: t(`${i18nKey}.filter.state.label`),
            options: [
              {
                value: "",
                label: t(`${i18nKey}.filter.state.all`)
              },
              ...Object.values(JobState)
                .filter(jobState => jobState !== JobState.unknown)
                .map(jobState => ({
                  value: jobState,
                  label: t(`job.state.${jobState}`)
                }))
            ]
          },
          {
            type: "select",
            name: "trigger",
            label: t(`${i18nKey}.filter.trigger.label`),
            options: [
              {
                value: "",
                label: t(`${i18nKey}.filter.state.all`)
              },
              ...Object.values(JobTrigger).map(jobTrigger => ({
                value: jobTrigger,
                label: t(`job.trigger.${jobTrigger}`)
              }))
            ]
          }
        ]}
        columns={[
          {
            field: "id",
            title: t(`${i18nKey}.column.id`),
            order: "desc"
          },
          {
            field: "name",
            title: t(`${i18nKey}.column.name`),
            sorting: false
          },
          {
            field: "createdAt",
            title: t(`${i18nKey}.column.createdAt`),
            render: job => job.createdAt.format("DD.MM.YYYY H:mm:ss")
          },
          {
            field: "startedAt",
            title: t(`${i18nKey}.column.startedAt`),
            render: job =>
              job.startedAt ? job.startedAt.format("DD.MM.YYYY H:mm:ss") : "---"
          },
          {
            field: "finishedAt",
            title: t(`${i18nKey}.column.finishedAt`),
            render: job =>
              job.finishedAt
                ? job.finishedAt.format("DD.MM.YYYY H:mm:ss")
                : "---"
          },
          {
            field: "application",
            title: t(`${i18nKey}.column.application`),
            sorting: false,
            render: job => (
              <>
                {job.application
                  ? `${job.application.name} (${job.application.id})`
                  : "---"}
              </>
            )
          },
          {
            field: "parentJob",
            title: t(`${i18nKey}.column.parentJob`),
            sorting: false,
            render: job => <>{job.parentJob ? `${job.parentJob.id}` : "---"}</>
          },
          {
            field: "handler",
            title: t(`${i18nKey}.column.handler`),
            sorting: false
          },
          {
            field: "trigger",
            title: t(`${i18nKey}.column.trigger`),
            sorting: false
          },
          {
            field: "state",
            title: t(`${i18nKey}.column.state`),
            render: job => (
              <JobStateChip
                job={(job as unknown) as Job}
                onClick={() =>
                  drawerService.activate("preview-job", {
                    job
                  })
                }
              />
            )
          }
        ]}
      />
    </div>
  );
}

export default wire(
  ["jobService", "drawerService"],
  withTranslation()(JobList)
);
